import * as ToggleGroupPrimitive from "@radix-ui/react-toggle-group";
import * as React from "react";

import { useDialogGuard } from "@/hooks/use-dialog-guard";
import { cn } from "@/lib/utils";

/**
 * Design 14j — a segmented control: the active item lifts out of the muted
 * trough onto a card. Used both as a view switch and for a small closed set of
 * persisted modes (third-party modes, a flow's status); a long list or a value
 * inside a larger form reads better as a RadioGroup or a Select.
 */
const ToggleGroup = React.forwardRef<
  React.ElementRef<typeof ToggleGroupPrimitive.Root>,
  React.ComponentPropsWithoutRef<typeof ToggleGroupPrimitive.Root>
>(({ className, ...props }, ref) => (
  <ToggleGroupPrimitive.Root
    ref={ref}
    className={cn(
      "inline-flex h-10 items-center justify-center gap-0.5 rounded-lg bg-muted p-1 text-muted-foreground",
      className,
    )}
    {...props}
  />
));
ToggleGroup.displayName = ToggleGroupPrimitive.Root.displayName;

const ToggleGroupItem = React.forwardRef<
  React.ElementRef<typeof ToggleGroupPrimitive.Item>,
  React.ComponentPropsWithoutRef<typeof ToggleGroupPrimitive.Item>
>(({ className, onClick, ...props }, ref) => {
  // QL-760 — a value inside a dialog form: tell the dialog it was edited (see
  // use-dialog-guard). Space / Enter on the item dispatch a click as well.
  // Only an item that was off declares an edit here: re-clicking the active one
  // emits "" in single mode, which callers ignore. The dialog's click heuristic
  // skips that re-click too (a checked radio, see dialog.tsx), and it is what
  // reports turning an item off in multiple mode. This handler runs before
  // Radix flips the item, so data-state is still the state before the click.
  const { markDirty } = useDialogGuard();
  return (
    <ToggleGroupPrimitive.Item
      ref={ref}
      onClick={(event) => {
        if (event.currentTarget.dataset.state !== "on") markDirty();
        onClick?.(event);
      }}
      className={cn(
        "inline-flex h-8 items-center justify-center gap-1.5 whitespace-nowrap rounded-md px-3 text-sm font-medium ring-offset-background transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 data-[state=on]:bg-card data-[state=on]:text-foreground data-[state=on]:shadow-sm [&_svg]:h-3.5 [&_svg]:w-3.5 [&_svg]:shrink-0",
        className,
      )}
      {...props}
    />
  );
});
ToggleGroupItem.displayName = ToggleGroupPrimitive.Item.displayName;

export { ToggleGroup, ToggleGroupItem };
